<template>
  <div class="cancellation-survey-form">
    <h5 class="cancellation-survey-form__sub-headline" ref="subheadline" v-html="subHeadlineCopy" />
    <notification type="error" class="cancellation-survey-form__error-box" v-if="error">
      {{ $t(error) }}
    </notification>
    <radio
      v-for="surveyOption in surveyOptions"
      :key="surveyOption"
      :name="surveyOption"
      v-model="selectedOption"
      class="mb-6"
      group-name="reason"
    >
      {{ $t(`options.${surveyOption}`) }}
    </radio>
    <notification type="notification" class="cancellation-survey-form__info-box">
      {{ infoTextCopy }}
    </notification>
    <div class="cancellation-survey-form__buttons">
      <button-component
        :modifiers="['midnight']"
        @click="submit"
        :disabled="ctaDisabled"
        is-cta
        class="cancellation-survey-form__cta"
      >
        {{ buttonCopy }}
      </button-component>
      <a class="cancellation-survey-form__keep-subscription" :href="settingsPath">{{ $t("keepSubscription") }}</a>
    </div>
  </div>
</template>

<script>
import Notification from "@/shared/notification"
import ButtonComponent from "@/shared/button"
import Radio from "@/shared/form/radio"
import globals from "@/utils/globals"
import api from "@/api"
import { go } from "@/utils/location"
import getLocale from "@/utils/locale"

export default {
  name: "CancellationSurveyForm",
  components: {
    Notification,
    ButtonComponent,
    Radio
  },
  props: {
    surveyOptions: {
      type: Array,
      default: () => []
    },
    eligibleForChurnPrevention: {
      type: Boolean,
      default: false
    },
    isLeavingMup: {
      type: Boolean,
      default: false
    },
    sku: {
      type: String,
      default: null
    }
  },
  data: () => ({
    selectedOption: null,
    error: null,
    loading: false
  }),
  computed: {
    settingsPath() {
      return globals.routes.settingsPath
    },
    subHeadlineCopy() {
      return this.$i18n.t(`subheadline.${this.isLeavingMup ? "multiUserPlan" : "default"}`)
    },
    buttonCopy() {
      if (this.isLeavingMup) {
        return this.$i18n.t("terminateSubscription.leaveMultiUserPlan")
      } else if (this.eligibleForChurnPrevention) {
        return this.$i18n.t("terminateSubscription.next")
      } else {
        return this.$i18n.t("terminateSubscription.terminate")
      }
    },
    infoTextCopy() {
      return this.$i18n.t(`infoText.${this.isLeavingMup ? "multiUserPlan" : "default"}`)
    },
    ctaDisabled() {
      return !this.selectedOption || this.loading
    }
  },
  methods: {
    submit() {
      if (!this.selectedOption) {
        this.error = "errors.noOptionSelected"
        this.$refs.subheadline.scrollIntoView({ behavior: "smooth" })
        return
      }

      if (this.eligibleForChurnPrevention) {
        go(`/${getLocale()}/nc/stay?v=${this.selectedOption}`)
        return Promise.resolve()
      } else {
        this.error = null
        this.loading = true
        return api.settings
          .cancelSubscription(this.selectedOption, this.sku)
          .then(() => {
            go(globals.routes.settingsPath)
          })
          .catch(() => {
            this.loading = false
            this.error = "errors.generic"
            this.$refs.subheadline.scrollIntoView({ behavior: "smooth" })
          })
      }
    }
  },
  i18n: {
    messages: {
      en: {
        subheadline: {
          default:
            "We’re sorry you want to deactivate your subscription. To help us learn and improve, please tell us the reason why you wish to not renew your subscription.",
          multiUserPlan:
            "We're sorry you want to leave your shared plan. To help us learn and improve, please tell us the reason why you wish to leave."
        },
        infoText: {
          default:
            "Once your current subscription cycle ends, you will no longer be able to read and/or listen to your saved Blinks. You will only be able to access the Blink of the Day.",
          multiUserPlan:
            "You will no longer be able to read and/or listen to your saved Blinks. You will only be able to access the Blink of the Day. The owner of the shared plan can then invite someone else instead."
        },
        terminateSubscription: {
          terminate: "Cancel Subscription",
          next: "Next",
          leaveMultiUserPlan: "Leave shared plan"
        },
        keepSubscription: "Keep subscription",
        options: {
          /* eslint-disable camelcase */ too_expensive: "The subscription is too expensive for my budget.",
          too_short: "The Blinkist formats are just not for me, they’re too short / shallow.",
          content: "I didn’t like the content.",
          not_using_app: "I’m not using the app often enough.",
          basic: "The Basic plan with one free title a day is enough for me.",
          forget: "I worry that I’ll forget to cancel my trial.",
          short_trial: "I would need a longer free trial to discover the product.",
          features_are_missing: "Some features I expected are missing.",
          didnt_find_content: "I didn’t find the content I was looking for, or enough of it.",
          other: "Other" /* eslint-enable camelcase */
        },
        errors: {
          generic: "Something went wrong",
          noOptionSelected: "Please select one of the options"
        }
      },
      de: {
        subheadline: {
          default:
            "Schade, dass du gehst! Woran liegt's?<br>Jede Information hilft uns dabei, unsere App und Titel zu verbessern.",
          multiUserPlan:
            "Schade, dass du gehst! Woran liegt's?<br>Jede Information hilft uns dabei, unsere App und Titel zu verbessern."
        },
        infoText: {
          default:
            "Sobald dein Abo abgelaufen ist, endet der unbegrenzte Zugang zu Blinkist, auch zu deinen gespeicherten Titeln.",
          multiUserPlan:
            "Der unbegrenzte Zugang zu Blinkist, inklusive deiner gespeicherten Titel endet dann. Du kannst aber weiterhin auf das Free Daily zugreifen. Der Besitzer des geteilten Abonements kann dieses dann mit jemand anders teilen."
        },
        terminateSubscription: {
          terminate: "Kündigen",
          next: "Weiter",
          leaveMultiUserPlan: "Geteiltes Abo verlassen"
        },
        keepSubscription: "Abbrechen",
        options: {
          /* eslint-disable camelcase */ too_expensive: "Blinkist ist mir zu teuer.",
          too_short: "Die Blinkist Formate sind einfach nichts für mich, sie sind mir zu kurz/ oberflächlich.",
          content: "Mir haben die Inhalte nicht gefallen.",
          not_using_app: "Ich habe die App nicht oft genug genutzt.",
          basic: "Mir reicht das kostenlose Blinkist Basic (ein Titel pro Tag).",
          forget: "Ich wollte einfach nicht vergessen, das Probeabo zu kündigen.",
          short_trial: "Die Zeit des Probeabos war zu kurz, um mir wirklich ein Bild von Blinkist zu machen.",
          features_are_missing: "Mir haben bestimmte Funktionalitäten gefehlt.",
          didnt_find_content: "Ich habe nicht die Inhalte gefunden, die ich gesucht habe.",
          other: "Andere Gründe " /* eslint-enable camelcase */
        },
        errors: {
          generic: "Entschuldigung, etwas ist schief gelaufen.",
          noOptionSelected: "Bitte wähle eine der genannten Optionen"
        }
      },
      es: {
        subheadline: {
          default:
            "Lamentamos que quieras cancelar tu suscripción. Para ayudarnos a aprender y mejorar, dinos el motivo por el que deseas no renovar tu suscripción.",
          multiUserPlan:
            "Lamentamos que quieras dejar tu plan compartido. Para ayudarnos a aprender y mejorar, dinos el motivo por el que deseas irte."
        },
        infoText: {
          default:
            "Una vez que finalice tu ciclo de suscripción actual, ya no podrás leer ni escuchar tus Blinks guardados. Solo podrás acceder al Blink del día.",
          multiUserPlan:
            "Ya no podrás leer ni escuchar tus Blinks guardados. Solo podrás acceder al Blink del día. El propietario del plan compartido podrá invitar a otra persona en tu lugar."
        },
        terminateSubscription: {
          terminate: "Cancelar suscripción",
          next: "Siguiente",
          leaveMultiUserPlan: "Dejar plan compartido"
        },
        keepSubscription: "Mantener suscripción",
        options: {
          /* eslint-disable camelcase */ too_expensive: "La suscripción es demasiado cara para mi presupuesto.",
          too_short: "Los formatos de Blinkist no son para mí, son demasiado cortos/superficiales.",
          content: "No me gustó el contenido.",
          not_using_app: "No uso la aplicación con suficiente frecuencia.",
          basic: "El plan básico con un título gratuito al día es suficiente para mí.",
          forget: "Me preocupa olvidarme de cancelar mi prueba.",
          short_trial: "Necesitaría una prueba gratuita más larga para descubrir el producto.",
          features_are_missing: "Faltan algunas funciones que esperaba.",
          didnt_find_content: "No encontré el contenido que buscaba, o no había suficiente.",
          other: "Otro" /* eslint-enable camelcase */
        },
        errors: {
          generic: "Algo salió mal",
          noOptionSelected: "Por favor selecciona una de las opciones"
        }
      },
      pt: {
        subheadline: {
          default:
            "Lamentamos que pretendas desativar a tua subscrição. Para nos ajudar a aprender e melhorar, indica-nos o motivo pelo qual desejas não renovar a tua subscrição.",
          multiUserPlan:
            "Lamentamos que pretendas deixar o teu plano partilhado. Para nos ajudar a aprender e melhorar, indica-nos o motivo pelo qual desejas sair."
        },
        infoText: {
          default:
            "Assim que o teu ciclo de subscrição atual terminar, deixarás de poder ler e/ou ouvir os teus Blinks guardados. Só poderás aceder ao Blink do Dia.",
          multiUserPlan:
            "Deixarás de poder ler e/ou ouvir os teus Blinks guardados. Só poderás aceder ao Blink do Dia. O proprietário do plano partilhado poderá então convidar outra pessoa."
        },
        terminateSubscription: {
          terminate: "Cancelar subscrição",
          next: "Seguinte",
          leaveMultiUserPlan: "Sair do plano partilhado"
        },
        keepSubscription: "Manter subscrição",
        options: {
          /* eslint-disable camelcase */ too_expensive: "A subscrição é demasiado cara para o meu orçamento.",
          too_short: "Os formatos do Blinkist não são para mim, são demasiado curtos/superficiais.",
          content: "Não gostei do conteúdo.",
          not_using_app: "Não uso a aplicação com frequência suficiente.",
          basic: "O plano Básico com um título gratuito por dia é suficiente para mim.",
          forget: "Preocupo-me em esquecer-me de cancelar o meu período de teste.",
          short_trial: "Precisaria de um período de teste gratuito mais longo para descobrir o produto.",
          features_are_missing: "Faltam algumas funcionalidades que esperava.",
          didnt_find_content: "Não encontrei o conteúdo que procurava, ou não havia suficiente.",
          other: "Outro" /* eslint-enable camelcase */
        },
        errors: {
          generic: "Algo correu mal",
          noOptionSelected: "Por favor seleciona uma das opções"
        }
      }
    }
  }
}
</script>

<style lang="scss">
.cancellation-survey-form {
  &__sub-headline {
    @include font-style(h5);

    margin-top: $spacing-32;
    margin-bottom: $spacing-32;

    @include media(
      (
        min-width: m
      )
    ) {
      margin-bottom: $spacing-48;
    }
  }

  &__error-box {
    margin-bottom: $spacing-32;
  }

  &__info-box {
    margin-top: $spacing-32;
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  // `#{&} &__cta` -> This little trick creates a greater specificity of this CSS selector.
  // Only then align-self: center; will not be overwritten by the default styling from the plain
  // `cta` class and correctly center itself.
  #{&} &__cta {
    align-self: center;
    margin-top: $spacing-32;

    @include media(
      (
        min-width: m
      )
    ) {
      margin-top: $spacing-48;
    }
  }

  &__keep-subscription {
    @include font-weight(medium);

    margin-top: $spacing-16;
    margin-bottom: $spacing-64;

    text-decoration: underline;
  }
}
</style>
